// The population widgets' group on ?live=1 (Track 2 demo push, item 2): the players a Ranking
// ranks — and next Filter, Comparison, Similarity and Radar — read from the nightly
// Stats files (T2-12a). One shared loader so those widgets all start from the same group, the
// same way.
//
// The decided defaults (Prateek, 2026-10-15):
// - The group = the canvas player's league, her current season: the league the Stats tile
//   opens on for her (same steps — `live/stats.ts`). Q-010 is parked, so "her league" = that
//   league's file only, not every league.
// - Gender comes with it: a league's file is one gender (rules.md: women only with women).
// - Another season = that league's file for it, if built; older men's seasons aren't built until
//   Paul's refill — the widget says so, never guesses.
// - A player who changed league mid-season is left out of the league she left (Q-007, the
//   season's movers list) — as the Stats tile does.
//
// The output is the snapshot's own shape (`players[key].seasons[code].stats`, + name, team,
// country, dob, positionTemplate), so Ben's pure engines (computeRanking, computeFilterMatches,
// similarityResultsFor, radarRange) run on it unchanged. No caching beyond each widget's own live
// answers (live/query.ts): sharing between widgets is Track 3.

import type { StatsFile, StatsMoversFile } from "@catbento/shared";
import type { RawPlayerRecord } from "../data/snapshot.js";
import type { CompetitionId } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import { getFilterOptions } from "../engines/stats-filter-kit.js";
import type { LiveApi } from "./api.js";
import { currentSeasonOf, loadSeasonCompetitions, loadStatsMeta } from "./stats.js";
import { groupRowsOf, leftOut, loadMovers, loadStatsFile, seasonLabel } from "./stats-files.js";

// --- where the group comes from ------------------------------------------------------

/** The canvas player's league in her current season — the group's default source. */
export interface HerLeague {
  /** Null = no league that season (only national-team / international games). */
  competitionId: CompetitionId | null;
  /** League Table's label ("FAWSL", or the DB name); null with no league. */
  label: string | null;
  /** Her current season (the group's default season). */
  seasonId: number;
  /** Her club now — a new Club chip's first value when she isn't in the file shown. */
  teamName: string | null;
}

/**
 * Her league + current season, by the Stats tile's steps (`live/stats.ts`): her current season,
 * then her league in it. Her current league already IS that (seasonLeague's first rule), so the
 * common case needs no `/player-competitions` call; otherwise the tile's own fallback runs. Null =
 * she has no seasons at all.
 */
export async function loadHerLeague(
  api: LiveApi,
  playerId: PlayerId,
  snapshotLeagueNames: ReadonlyMap<CompetitionId, string>,
): Promise<HerLeague | null> {
  const meta = await loadStatsMeta(api, playerId, snapshotLeagueNames);
  const seasonCode = currentSeasonOf(meta);
  if (seasonCode === "") return null;
  const competitionId =
    meta.current.competitionId !== null && String(meta.current.seasonId) === seasonCode
      ? meta.current.competitionId
      : (await loadSeasonCompetitions(api, playerId, meta, seasonCode)).league;
  const label = competitionId === null ? null : (meta.leagueLabels.get(competitionId) ?? null);
  // Every league she plays in is in /competitions; if not, the routes disagree — thrown, as the
  // Stats tile does, never a nameless group.
  if (competitionId !== null && label === null) throw new Error(`Player ${playerId}: league ${competitionId} isn't in /competitions`);
  return { competitionId, label, seasonId: Number(seasonCode), teamName: meta.current.teamName };
}

/** The season to show: the one asked for if that league has its file, else her current season.
 *  `built` = false when even that isn't built (the widget says so). Pure. */
export function pickGroupSeason(
  asked: string | null,
  herSeason: number,
  builtSeasons: readonly number[],
): { seasonId: number; built: boolean } {
  const want = asked === null ? NaN : Number(asked);
  if (builtSeasons.includes(want)) return { seasonId: want, built: true };
  return { seasonId: herSeason, built: builtSeasons.includes(herSeason) };
}

/** The words when there's no group to show (the Stats tile's "No comparison yet" style). */
export function noGroupLabel(league: HerLeague, seasonId: number): string {
  return league.competitionId === null
    ? `No league for this player in ${seasonLabel(league.seasonId)}`
    : `${league.label} ${seasonLabel(seasonId)} isn't built yet`;
}

// --- one season's file ----------------------------------------------------------------

/** One league-season: its Stats file + that season's movers list (null = none yet, e.g. a
 *  season not rebuilt since 2b — then nobody is left out, as on the Stats tile). */
export interface GroupFile {
  file: StatsFile;
  movers: StatsMoversFile | null;
}

/** The file, then its season's movers (the file says the gender). Null = not built (404). */
export async function loadGroupFile(api: LiveApi, competitionId: CompetitionId, seasonId: number): Promise<GroupFile | null> {
  const file = await loadStatsFile(api, competitionId, seasonId);
  if (file === null) return null;
  const movers = await loadMovers(api, seasonId, file.womens);
  return { file, movers };
}

// --- the file → the snapshot's player shape -------------------------------------------

/**
 * Which rows become players:
 * - "total": one per player — all her minutes in the league (the default).
 * - "position": one per player per position group she played — only her minutes AT it (Ben's
 *   Position chip rule; two positions = two entries, Q-014). Keyed `<id>:<group>`.
 */
export type GroupPick = "total" | "position";

/** A group player, in the snapshot's shape (`RawPlayerRecord`), plus her id as a number. */
export interface GroupPlayer extends RawPlayerRecord {
  id: string;
  playerId: PlayerId;
  name: string;
  currentSeason: string;
  /** The season's numbers, also at the top level — Ben's chip code reads `stats` there
   *  (minutes, preferred foot). Under the snapshot's metric keys + `minutes`, `nineties_played`. */
  stats: Record<string, number | null>;
  seasons: Record<string, { stats: Record<string, number | null> }>;
}

/** Her main position here: the position group she played the most minutes at (Similarity and
 *  Radar read `positionTemplate`; the live Radar opens on it). Ties keep the file's order. */
export function mainPositions(file: StatsFile): Map<number, string> {
  const best = new Map<number, { group: string; minutes: number }>();
  for (const [playerId, kind, group, , minutes] of file.rows) {
    if (kind !== "group" || group === null) continue;
    const b = best.get(playerId);
    if (!b || minutes > b.minutes) best.set(playerId, { group, minutes });
  }
  return new Map([...best].map(([id, b]) => [id, b.group]));
}

/**
 * The group as snapshot-shaped players, keyed by id (`"15620"`, or `"15620:full_back"` for
 * position rows). `metricKeys` = the snapshot keys the widget needs (the engine's names are
 * mapped, and a key the file lacks throws — the engine-mismatch gotcha); keep it to what the
 * widget shows, so one renamed engine key can't break every widget. Movers who left this league
 * are left out. Pure.
 */
export function groupPlayers(
  group: GroupFile,
  pick: GroupPick,
  metricKeys: string[],
  leagueLabel: string,
): Record<string, GroupPlayer> {
  const { file } = group;
  const seasonCode = String(file.seasonId);
  const out = leftOut(group.movers, file.competitionId);
  const main = pick === "total" ? mainPositions(file) : null;
  const players: Record<string, GroupPlayer> = {};
  for (const row of groupRowsOf(file, { kind: pick === "total" ? "total" : "group" }, metricKeys)) {
    if (out.has(row.playerId)) continue;
    const who = file.players[String(row.playerId)];
    if (!who) throw new Error(`Stats file ${file.competitionId}/${file.seasonId}: player ${row.playerId} has rows but no name`);
    const position = main ? (main.get(row.playerId) ?? null) : (row.positionTemplate ?? null);
    const key = main ? String(row.playerId) : `${row.playerId}:${position}`;
    players[key] = {
      id: String(row.playerId),
      playerId: row.playerId,
      name: who.name,
      positionTemplate: position,
      team: row.team ?? null,
      competition: leagueLabel,
      // Kept apart from `competition`, which a position row relabels "at Midfielder" for display:
      // the League chip filters on this (Ben, 2026-10-16: Similarity went empty with both chips).
      league: leagueLabel,
      country: row.country ?? null,
      dob: row.dob ?? null,
      currentSeason: seasonCode,
      stats: row.stats,
      seasons: { [seasonCode]: { stats: row.stats } },
    };
  }
  return players;
}

/** The chip values this file offers (Position from every position row; Club, Nationality). League
 *  and Preferred Foot stay empty: League waits for Q-010, Foot isn't worked out on real data yet. */
export function groupChipOptions(group: GroupFile): Record<string, string[]> {
  return getFilterOptions(groupRowsOf(group.file, { kind: "group" }, []));
}

/** "N players" — distinct players (position rows can list one player twice). Pure. */
export function distinctPlayers(entries: readonly { playerId: number }[]): number {
  return new Set(entries.map((e) => e.playerId)).size;
}

/** A row as the "why twice" hover reads it: who, which position row ("at Full Back"), and her
 *  minutes there when known. */
export interface PositionRow {
  playerId: number;
  shortName: string;
  competition: string | null;
  positionMinutes?: number | null;
}

/** The key a row's hover is looked up by (one row per player + position). */
export const positionRowKey = (r: { playerId: number; competition: string | null }) => `${r.playerId}:${r.competition ?? ""}`;

/**
 * The "why twice" hover (Ben, Q-014): a player listed once per position (two positions on the
 * Position chip) says why, e.g. "Declan Rice, at Defensive Midfielder (450 min). Also in this
 * group at Centre Midfielder." Only for players listed more than once. Pure.
 */
export function whyTwice(rows: readonly PositionRow[]): Map<string, string> {
  const byPlayer = new Map<number, PositionRow[]>();
  for (const r of rows) {
    if (!r.competition?.startsWith("at ")) continue;
    const list = byPlayer.get(r.playerId) ?? [];
    list.push(r);
    byPlayer.set(r.playerId, list);
  }
  const out = new Map<string, string>();
  for (const list of byPlayer.values()) {
    if (list.length < 2) continue;
    for (const r of list) {
      const others = list.filter((o) => o !== r).map((o) => o.competition!.slice(3));
      const mins = r.positionMinutes == null ? "" : ` (${Math.round(r.positionMinutes).toLocaleString()} min)`;
      out.set(positionRowKey(r), `${r.shortName}, ${r.competition}${mins}. Also in this group at ${others.join(" and ")}.`);
    }
  }
  return out;
}
